import type { DeckSourceProviderId } from "@/types/deckSource";

export interface DeckSourceProviderInfo {
  id: DeckSourceProviderId;
  displayName: string;
  /** True when MVP supports importing from this provider (manual paste / local templates). */
  supportedNow: boolean;
  description: string;
}

export const DECK_SOURCE_PROVIDERS: DeckSourceProviderInfo[] = [
  {
    id: "manual",
    displayName: "Manual paste",
    supportedNow: true,
    description:
      "Paste a deck list copied from a TCGplayer deck article or any text source.",
  },
  {
    id: "local-template",
    displayName: "Saved template",
    supportedNow: true,
    description: "Use a deck-list template you've saved in this app.",
  },
  {
    id: "tcgplayer",
    displayName: "TCGplayer (coming soon)",
    supportedNow: false,
    description: "Direct fetch from TCGplayer deck pages — not enabled in MVP.",
  },
  {
    id: "pokemoncardio",
    displayName: "PokemonCard.io (coming soon)",
    supportedNow: false,
    description: "Phase 4 provider integration — not enabled in MVP.",
  },
  {
    id: "limitless",
    displayName: "Limitless (coming soon)",
    supportedNow: false,
    description: "Phase 4 provider integration — not enabled in MVP.",
  },
];

export interface DeckSourceProvider {
  id: DeckSourceProviderId;
  displayName: string;
  /**
   * Fetch a deck-list string from a provider URL. Manual / local-template
   * providers are no-ops here and rely on the UI capturing the paste/local
   * data directly.
   */
  fetchDeck(url: string): Promise<string>;
}

class NotImplementedProvider implements DeckSourceProvider {
  constructor(
    public readonly id: DeckSourceProviderId,
    public readonly displayName: string,
  ) {}
  async fetchDeck(): Promise<string> {
    throw new Error(
      `${this.displayName} provider is not enabled in this build. Configure a Phase 4 provider before using fetchDeck.`,
    );
  }
}

class ManualProvider implements DeckSourceProvider {
  readonly id: DeckSourceProviderId = "manual";
  readonly displayName = "Manual paste";
  async fetchDeck(): Promise<string> {
    throw new Error(
      "Manual provider does not fetch — pass the pasted deck list directly to parseDeckList.",
    );
  }
}

class LocalTemplateProvider implements DeckSourceProvider {
  readonly id: DeckSourceProviderId = "local-template";
  readonly displayName = "Saved template";
  async fetchDeck(): Promise<string> {
    throw new Error(
      "Local template provider does not fetch — pass the resolved template body directly to parseDeckList.",
    );
  }
}

const REGISTRY: Record<DeckSourceProviderId, DeckSourceProvider> = {
  manual: new ManualProvider(),
  "local-template": new LocalTemplateProvider(),
  tcgplayer: new NotImplementedProvider("tcgplayer", "TCGplayer"),
  pokemoncardio: new NotImplementedProvider("pokemoncardio", "PokemonCard.io"),
  limitless: new NotImplementedProvider("limitless", "Limitless"),
};

export function getDeckSourceProvider(
  id: DeckSourceProviderId,
): DeckSourceProvider {
  return REGISTRY[id];
}
